import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { cn } from '@/lib/utils';
import { apiPost, apiPatch } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ProjectFormData } from '@/types';

// Force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};

interface NewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateProject?: (project: { id: string; name: string }) => void;
  onUpdateProject?: (project: {
    id: string;
    name: string;
    description: string;
    instructions: string;
  }) => void;
  onShowToast?: (
    message: string,
    type?: 'success' | 'error' | 'info' | 'warning',
    variant?: 'default' | 'destructive' | 'success'
  ) => void;
  // 编辑模式相关
  mode?: 'create' | 'edit';
  projectData?: ProjectFormData;
}

export default function NewProjectModal({
  isOpen,
  onClose,
  onCreateProject,
  onUpdateProject,
  onShowToast,
  mode = 'create',
  projectData,
}: NewProjectModalProps) {
  const { t } = useTranslation();
  useForceUpdate();
  const [projectName, setProjectName] = useState('');
  const [describe, setDescribe] = useState('');
  const [instructions, setInstructions] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = mode === 'edit';

  // 编辑模式下，初始化表单数据
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && projectData) {
        setProjectName(projectData.name || '');
        setDescribe(projectData.description || '');
        setInstructions(projectData.instructions || '');
      } else {
        // 创建模式，清空表单
        setProjectName('');
        setDescribe('');
        setInstructions('');
      }
    }
  }, [isOpen, isEditMode, projectData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (projectName.trim() && !isSubmitting) {
      setIsSubmitting(true);
      try {
        if (isEditMode && projectData) {
          // 编辑模式：调用 PATCH API
          await apiPatch(`/api/projects/${projectData.id}`, {
            name: projectName.trim() || null,
            description: describe.trim() || null,
            instructions: instructions.trim() || null,
          });

          onShowToast?.(
            t('project.updateSuccess', { name: projectName.trim() }),
            'success',
            'success'
          );
          onUpdateProject?.({
            id: projectData.id,
            name: projectName.trim(),
            description: describe.trim(),
            instructions: instructions.trim(),
          });
          onClose();
        } else {
          // 创建模式：调用 POST API
          const result = await apiPost<any>('/api/projects', {
            projectName: projectName.trim(),
            description: describe.trim(),
          });

          if (result.code === 200) {
            onShowToast?.(
              t('project.createSuccess', { name: result.projectName }),
              'success',
              'success'
            );
            onCreateProject?.({
              id: result.projectId,
              name: result.projectName,
            });
            setProjectName('');
            setDescribe('');
            setInstructions('');
            onClose();
          } else {
            throw new Error(result.message || 'Failed to create project');
          }
        }
      } catch (error) {
        console.error(
          `Error ${isEditMode ? 'updating' : 'creating'} project:`,
          error
        );
        // const errorMessage =
        //   error instanceof Error ? error.message : 'Unknown error';
        // onShowToast?.(
        //   `Failed to ${isEditMode ? 'update' : 'create'} project: ${errorMessage}`,
        //   'error',
        //   'destructive'
        // );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? t('project.editProject') : t('project.createProject')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          {/* 🔥 body 内容区域添加内边距 */}
          <div className="px-6 py-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isEditMode
                  ? t('project.projectName')
                  : t('project.projectNameLabel')}
              </label>
              <input
                type="text"
                value={projectName}
                onChange={e => setProjectName(e.target.value)}
                placeholder={t('project.projectNamePlaceholder')}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500 text-gray-900 placeholder-gray-400"
                autoFocus
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isEditMode
                  ? t('form.description')
                  : t('project.descriptionLabel')}
              </label>
              <textarea
                value={describe}
                onChange={e => setDescribe(e.target.value)}
                placeholder={t('project.descriptionPlaceholder')}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-500 text-gray-900 placeholder-gray-400 resize-none"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!projectName.trim() || isSubmitting}
              className={cn(
                !projectName.trim() || isSubmitting
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : ''
              )}
            >
              {isSubmitting
                ? isEditMode
                  ? t('project.saving')
                  : t('project.creating')
                : isEditMode
                  ? t('common.save')
                  : t('common.confirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
